import CloseIcon from '@mui/icons-material/Close';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { isSameDay, isValid } from 'date-fns';
import get from 'lodash/get';
import { ReactElement, useState } from 'react';
import DateRangePicker from 'react-datepicker';
import { Control, Controller, FieldErrors, FieldValues, Path } from 'react-hook-form';
import classNames from 'classnames';

import 'react-datepicker/dist/react-datepicker.css';

import ensureDate from '~/utils/date/ensureDate';

import { ReactComponent as CalendarIcon } from '~/ui/assets/images/calendarIcon.svg';
import { generalDate } from '~/ui/constants/dateFormat';

import formatRangeText from './formatRangeText';
import { DateRangeValue } from './types';
import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './DateRangePicker.module.scss';

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors;
  name: string;
  minDate?: string;
  maxDate?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  dateFormat?: string;
  showYearDropdown?: boolean;
  size?: FormControlProps['size'];
  formControlClassName?: string;
  deps?: Path<TFieldValues>[];
  isClearable?: boolean;
  hideOnSelect?: boolean;
  showSingleDate?: boolean;
}

const CustomDateRangePicker = <TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  name,
  minDate,
  maxDate,
  label = '',
  placeholder = '',
  disabled = false,
  dateFormat = generalDate,
  formControlClassName,
  size = 'small',
  showYearDropdown,
  deps,
  isClearable = false,
  hideOnSelect = false,
  showSingleDate = false,
}: IProps<TFieldValues>): ReactElement => {
  const error = get(errors, `${name}.message`, '');
  const isError = Boolean(error);
  const [typedText, setTypedText] = useState<string | null>(null);

  return (
    <Controller
      control={control}
      name={name as Path<TFieldValues>}
      rules={{ deps }}
      render={({ field: { value: rawValue, onChange } }) => {
        const value = rawValue as DateRangeValue | undefined;
        const showClear = isClearable && !disabled && Boolean(value?.[0] || value?.[1]);
        const displayText = showSingleDate
          ? typedText ??
            formatRangeText([ensureDate(value?.[0]), ensureDate(value?.[1])], dateFormat)
          : undefined;

        return (
          <FormControl
            variant="outlined"
            size={size}
            fullWidth
            error={isError}
            className={classNames(styles.datePickerControl, formControlClassName)}
          >
            {label && (
              <InputLabel shrink error={isError}>
                {label}
              </InputLabel>
            )}
            <DateRangePicker
              value={displayText}
              onChange={dates => {
                setTypedText(null);
                // fix for selection same date for range
                const update = dates.map(v => ensureDate(v)) as [Date, Date];
                const firstSelected = !!value && value[0] && !value[1];
                const sameDay = !!value && isSameDay(value[0], update[0]);

                if (firstSelected && sameDay && !update[1]) {
                  onChange([update[0], update[0]]);
                } else {
                  onChange(update);
                }
              }}
              onChangeRaw={event => {
                setTypedText(event.target.value ?? null);
                const dates = event.target.value?.split(' - ');
                const isValidDates = dates
                  ? isValid(new Date(dates[0])) && isValid(new Date(dates[1]))
                  : false;
                const from = isValidDates ? new Date(dates[0]) : null;
                const to = isValidDates ? new Date(dates[1]) : null;
                if (isValidDates) {
                  onChange([from, to], event);
                }
              }}
              startDate={ensureDate(value?.[0])}
              endDate={ensureDate(value?.[1])}
              placeholderText={placeholder}
              disabled={disabled}
              selectsRange
              shouldCloseOnSelect={hideOnSelect}
              onBlur={() => setTypedText(null)}
              onCalendarClose={() => setTypedText(null)}
              calendarClassName={styles.calendar}
              customInput={
                <OutlinedInput
                  endAdornment={
                    <>
                      {showClear && (
                        <IconButton
                          aria-label="Clear dates"
                          size="small"
                          className={styles.clearButton}
                          onClick={event => {
                            event.stopPropagation();
                            setTypedText(null);
                            onChange([null, null]);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      <CalendarIcon color={variables.colorRed} />
                    </>
                  }
                  fullWidth
                  notched={!!label}
                  label={label}
                  className={isError ? styles.withError : undefined}
                />
              }
              minDate={minDate ? new Date(minDate) : undefined}
              maxDate={maxDate ? new Date(maxDate) : undefined}
              dateFormat={dateFormat}
              showYearDropdown={showYearDropdown}
            />

            {error && (
              <FormHelperText error className={styles.errorText}>
                {error.toString()}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default CustomDateRangePicker;
